import { CartLine } from '@msdyn365-commerce/retail-proxy';
import { observable, action, computed } from 'mobx';
import debounce from 'lodash/debounce';

export type UpdateLineSerialBatcherSendBatch = (items: [string, number][]) => Promise<void>;

export default class UpdateLineSerialBatcher {
    private queued = new Map<string, number>();
    private inFlightKeys = observable.set<string>();
    private running = false;

    // kick is ALWAYS debounced
    private kick = debounce(() => {
        if (!this.running) {
            void this.run();
        }
    }, 1000);

    private sendBatch: UpdateLineSerialBatcherSendBatch;

    constructor(sendBatch: UpdateLineSerialBatcherSendBatch) {
        this.sendBatch = sendBatch;
    }

    public enqueue(cartLine: CartLine, quantity: number, _immediate = false) {
        if (!cartLine.LineId) {
            return;
        }

        this.queued.set(cartLine.LineId, quantity);
        this.kick(); // debounced trigger

        if (_immediate) {
            this.kick.flush();
        }
    }

    public isLocked(cartLine: CartLine) {
        if (!cartLine.LineId) {
            return false;
        }
        return this.inFlightKeys.has(cartLine.LineId);
    }

    @computed get inFlightLineIds() {
        return Array.from(this.inFlightKeys);
    }

    private async run() {
        this.running = true;

        while (this.queued.size) {
            const batch = Array.from(this.queued.entries());
            this.queued.clear();

            this.markInFlight(batch.map(([k]) => k));

            try {
                await this.sendBatch(batch);
            } finally {
                this.clearInFlight(batch.map(([k]) => k));
            }
        }

        this.running = false;
    }

    @action
    private markInFlight(keys: string[]) {
        this.inFlightKeys.clear();
        keys.forEach(k => this.inFlightKeys.add(k));
    }

    @action
    private clearInFlight(keys: string[]) {
        keys.forEach(k => this.inFlightKeys.delete(k));
    }
}
